import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Repeat, Users, Inbox, Send, CheckCircle, XCircle, Clock,
  AlertCircle, ChevronRight, X, ClipboardCheck, UserCheck, ShieldCheck, ArrowRightCircle,
  History, ClipboardList, ArrowLeft, Plus, Info,
} from 'lucide-react';
import toast from '../utils/toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Portal from '../components/UI/Portal';
import { StatusBadge } from '../components/UI/StatusBadge';
import FormSelect from '../components/UI/FormSelect';
import DatePickerField from '../components/UI/DatePickerField';
import PillToggle from '../components/UI/PillToggle';
import SearchableMultiCombobox from '../components/UI/SearchableMultiCombobox';
import { staffAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { TradeBadge } from '../components/UI/TradeBadge';

const TradeTransferPath = ({ from, to }) => (
  <span className="inline-flex items-center gap-1.5 flex-wrap justify-end">
    {from ? <TradeBadge name={from} size="xs" /> : <span className="text-gray-400">—</span>}
    <span className="text-gray-400">→</span>
    {to ? <TradeBadge name={to} size="xs" /> : <span className="text-gray-400">—</span>}
  </span>
);

// Map a per-item status to a StatusBadge color key (statusConfig has no
// pending_current_tl / pending_worker keys, so we route those to 'pending').
const ITEM_BADGE_STATUS = {
  pending_current_tl: 'pending',
  pending_worker: 'pending',
  pending_hod: 'pending_hod',
  approved: 'approved',
  completed: 'completed',
  rejected: 'rejected',
  cancelled: 'cancelled',
};

const GATE_LABEL = {
  pending_current_tl: 'Current Trade Leader',
  pending_worker: 'Worker',
  pending_hod: 'HOD',
};

// Human-readable rollup labels for the HR All Requests collapsed card.
// Never surface raw schema keys (e.g. "pending_current_tl") to end users.
const ITEM_STATUS_SUMMARY = {
  pending_current_tl: { badgeStatus: 'pending', phrase: 'awaiting Current Trade Leader' },
  pending_worker: { badgeStatus: 'pending', phrase: 'awaiting Worker' },
  pending_hod: { badgeStatus: 'pending_hod', phrase: 'awaiting HOD' },
  approved: { badgeStatus: 'approved', phrase: 'scheduled' },
  completed: { badgeStatus: 'completed', phrase: 'completed' },
  rejected: { badgeStatus: 'rejected', phrase: 'rejected' },
  cancelled: { badgeStatus: 'cancelled', phrase: 'cancelled' },
};

// ---------------------------------------------------------------------------
// Decision modal — used by the inbox to accept/approve or reject/decline.
// ---------------------------------------------------------------------------
const DecisionModal = ({ item, onClose, onDecided }) => {
  const [mode, setMode] = useState(null); // 'approve' | 'reject'
  const [comments, setComments] = useState('');

  const decideMutation = useMutation({
    mutationFn: ({ approve, text }) =>
      staffAPI.decideTradeTransferItem(item.id, { approve, comments: text }),
    onSuccess: () => {
      toast.success('Decision recorded.');
      onDecided();
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Failed to record decision.');
    },
  });

  const isWorkerGate = item.status === 'pending_worker';
  const approveWord = isWorkerGate ? 'Accept' : 'Approve';
  const rejectWord = isWorkerGate ? 'Decline' : 'Reject';

  const submit = () => {
    if (mode === 'reject' && !comments.trim()) {
      toast.error('A reason is required.');
      return;
    }
    decideMutation.mutate({ approve: mode === 'approve', text: comments });
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Trade Transfer Decision</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 py-4 space-y-3 overflow-y-auto">
            <div className="rounded-xl bg-gray-50 p-4 space-y-1.5 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Worker</span>
                <span className="font-medium text-gray-900 text-right">{item.worker_name}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">From → To</span>
                <span className="font-medium text-gray-900 text-right">
                  <TradeTransferPath from={item.from_trade_category_name} to={item.target_trade_name} />
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Effective</span>
                <span className="font-medium text-gray-900 text-right">{formatDate(item.effective_date)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">New RO</span>
                <span className="font-medium text-gray-900 text-right">
                  {item.target_reporting_officer_name
                    ? `${item.target_reporting_officer_name} · ${item.target_reporting_officer_staff_id}`
                    : 'Not recorded'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Requested by</span>
                <span className="font-medium text-gray-900 text-right">{item.requested_by_name}</span>
              </div>
              <div className="pt-1">
                <span className="text-gray-500">Reason</span>
                <p className="mt-0.5 text-gray-800">{item.reason}</p>
              </div>
            </div>

            {isWorkerGate && (
              <p className="text-xs text-gray-500">
                You are being asked to acknowledge your transfer. Accepting confirms you are aware;
                declining will stop the transfer.
              </p>
            )}

            {/* Action selector */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                type="button"
                onClick={() => setMode('approve')}
                className={`flex items-center justify-center gap-2 h-11 rounded-xl border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1 ${
                  mode === 'approve'
                    ? 'border-green-600 bg-green-50 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CheckCircle className="w-4 h-4" /> {approveWord}
              </button>
              <button
                type="button"
                onClick={() => setMode('reject')}
                className={`flex items-center justify-center gap-2 h-11 rounded-xl border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${
                  mode === 'reject'
                    ? 'border-red-600 bg-red-50 text-red-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <XCircle className="w-4 h-4" /> {rejectWord}
              </button>
            </div>

            {mode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mode === 'reject' ? `Reason for ${rejectWord.toLowerCase()} ` : 'Comments '}
                  {mode === 'reject'
                    ? <span className="text-red-600">*</span>
                    : <span className="text-gray-400">(optional)</span>}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={mode === 'reject' ? 'Explain why this transfer cannot proceed…' : 'Add a note…'}
                />
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t flex justify-end gap-3">
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button
              onClick={submit}
              disabled={!mode || decideMutation.isPending}
              className="btn btn-primary disabled:opacity-50"
            >
              {decideMutation.isPending ? 'Submitting…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// ---------------------------------------------------------------------------
// HR-directed transfer modal — HR / worker admins move workers into ANY trade,
// in either direction. Reuses the same approval chain as the TL pull path;
// only the initiation permission differs (HR need not lead the target trade).
// ---------------------------------------------------------------------------
const toISODate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// TradeTransferService raises a single `{error: "..."}` string for business-rule
// violations, but DRF serializer validation (e.g. a malformed field) instead
// returns a field-keyed dict — fall back to the first field error so those
// don't collapse into a generic, unhelpful toast.
const extractTtErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  const firstValue = Object.values(data)[0];
  return (Array.isArray(firstValue) ? firstValue[0] : firstValue) || fallback;
};

const HRInitiateModal = ({ onClose, onCreated }) => {
  const [targetTrade, setTargetTrade] = useState('');
  const [targetReportingOfficer, setTargetReportingOfficer] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [reason, setReason] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState([]);

  const { data: trades = [] } = useQuery({
    queryKey: ['tt-all-trades'],
    queryFn: () => staffAPI.getTradeTransferAllTrades().then((r) => r.data),
  });

  const { data: workers = [], isLoading: workersLoading } = useQuery({
    queryKey: ['tt-transferable-workers'],
    queryFn: () => staffAPI.getTradeTransferTransferableWorkers().then((r) => r.data),
  });

  const { data: reportingOfficers = [], isLoading: reportingOfficersLoading } = useQuery({
    queryKey: ['trade-transfer', 'eligible-reporting-officers', targetTrade],
    queryFn: () => staffAPI
      .getTradeTransferEligibleReportingOfficers(targetTrade)
      .then((r) => r.data),
    enabled: !!targetTrade,
  });

  const tradeOptions = useMemo(
    () => trades.map((t) => ({
      value: String(t.id),
      label: t.trade_leader_name ? `${t.name} · led by ${t.trade_leader_name}` : `${t.name} · no leader`,
    })),
    [trades],
  );

  const reportingOfficerOptions = useMemo(
    () => reportingOfficers.map((person) => ({
      value: person.staff_id,
      label: `${person.name} · ${person.staff_id}${person.designation ? ` · ${person.designation}` : ''}`,
    })),
    [reportingOfficers],
  );

  // Exclude workers already in the chosen destination (a no-op the backend
  // rejects) so an invalid selection is impossible rather than merely caught.
  // targetTrade / trade_category_id are both UUID strings — compare as-is.
  const availableWorkers = useMemo(() => {
    if (!targetTrade) return workers;
    return workers.filter((w) => w.trade_category_id !== targetTrade);
  }, [workers, targetTrade]);

  // Switching target trade drops any now-invalid (same-trade) selections.
  useEffect(() => {
    if (!targetTrade) return;
    setTargetReportingOfficer('');
    setSelectedWorkers((prev) => prev.filter((sid) => {
      const w = workers.find((x) => x.staff_id === sid);
      return !w || w.trade_category_id !== targetTrade;
    }));
  }, [targetTrade, workers]);

  const createMutation = useMutation({
    mutationFn: () => staffAPI.hrInitiateTradeTransfer({
      target_trade_category_id: targetTrade,
      target_reporting_officer_id: targetReportingOfficer,
      effective_date: toISODate(effectiveDate),
      reason: reason.trim(),
      worker_ids: selectedWorkers,
    }),
    onSuccess: () => {
      toast.success('Trade transfer initiated.');
      onCreated();
      onClose();
    },
    onError: (err) => toast.error(extractTtErrorMessage(err, 'Failed to initiate transfer.')),
  });

  const canSubmit = targetTrade && targetReportingOfficer
    && effectiveDate && reason.trim() && selectedWorkers.length > 0;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Initiate Trade Transfer</h2>
              <p className="text-xs text-gray-500 mt-0.5">HR-directed placement — into any trade, either direction.</p>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 py-4 space-y-4 overflow-y-auto">
            <div className="flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-100 p-3">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800 leading-relaxed">
                Each worker still flows through their current Trade Leader, the worker, and the HOD.
                Their selected Reporting Officer changes with the trade on the effective date.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transfer into trade <span className="text-red-600">*</span>
              </label>
              <FormSelect
                options={tradeOptions}
                value={targetTrade}
                onChange={setTargetTrade}
                placeholder="Select the destination trade…"
                theme="employee"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Reporting Officer <span className="text-red-600">*</span>
              </label>
              <FormSelect
                options={reportingOfficerOptions}
                value={targetReportingOfficer}
                onChange={setTargetReportingOfficer}
                placeholder={
                  reportingOfficersLoading
                    ? 'Loading Reporting Officers…'
                    : targetTrade
                      ? 'Select the destination trade RO…'
                      : 'Choose a destination trade first…'
                }
                theme="employee"
                disabled={!targetTrade || reportingOfficersLoading}
                required
              />
              {targetTrade && !reportingOfficersLoading && reportingOfficerOptions.length === 0 && (
                <p className="mt-1.5 text-xs text-amber-700">
                  No eligible Reporting Officer is configured for this trade.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Effective date <span className="text-red-600">*</span>
              </label>
              <DatePickerField
                theme="employee"
                selected={effectiveDate}
                onChange={setEffectiveDate}
                minDate={new Date()}
                placeholderText="Select date"
                isClearable
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workers to transfer <span className="text-red-600">*</span>
              </label>
              <SearchableMultiCombobox
                items={availableWorkers}
                value={selectedWorkers}
                onChange={setSelectedWorkers}
                valueFn={(w) => w.staff_id}
                displayFn={(w) => `${w.name} · ${w.trade_category_name}`}
                searchFn={(w, term) =>
                  `${w.name} ${w.staff_id} ${w.trade_category_name}`.toLowerCase().includes(term)}
                groupFn={(w) => w.trade_category_name}
                placeholder={targetTrade ? 'Select workers from any other trade…' : 'Choose a destination trade first…'}
                searchPlaceholder="Search by name, staff ID, or trade…"
                icon={Users}
                iconColor="text-blue-700"
                theme="employee"
                variant="form"
                loading={workersLoading}
                disabled={!targetTrade}
                showChips
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for transfer <span className="text-red-600">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Explain why these workers are being transferred…"
              />
            </div>
          </div>

          <div className="px-5 py-4 border-t flex justify-end gap-3">
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit || createMutation.isPending}
              className="btn btn-primary disabled:opacity-50"
            >
              {createMutation.isPending ? 'Submitting…' : 'Initiate Transfer'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// ---------------------------------------------------------------------------
// Per-item chain row used in request detail.
// ---------------------------------------------------------------------------
const ChainStep = ({ label, status, by, at, comments }) => {
  const icon = status === 'approved'
    ? <CheckCircle className="w-4 h-4 text-green-600" />
    : status === 'rejected'
      ? <XCircle className="w-4 h-4 text-red-600" />
      : <Clock className="w-4 h-4 text-amber-500" />;
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5">{icon}</div>
      <div className="text-xs">
        <p className="font-medium text-gray-800">{label}</p>
        <p className="text-gray-500">
          {status === 'pending' ? 'Awaiting decision' : `${status === 'approved' ? 'Approved' : 'Rejected'}${by ? ` by ${by}` : ''}${at ? ` · ${formatDate(at)}` : ''}`}
        </p>
        {comments && <p className="text-gray-600 mt-0.5 italic">“{comments}”</p>}
      </div>
    </div>
  );
};

const RequestCard = ({ request, onCancel, canCancel }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-5 py-4 flex items-start justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{request.request_number || 'Draft'}</span>
            <StatusBadge status={request.status} label={request.status_display} />
          </div>
          <p className="text-sm text-gray-600 mt-1">
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              {request.worker_count} {request.worker_count === 1 ? 'worker' : 'workers'}
              <span className="text-gray-400">→</span>
              <TradeBadge name={request.target_trade_name} size="xs" />
            </span>
            {' · '}Effective {formatDate(request.effective_date)}
          </p>
        </div>
        <ChevronRight className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-5 pb-4 border-t pt-4 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</p>
            <p className="text-sm text-gray-800 mt-1">{request.reason}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              New Reporting Officer
            </p>
            <p className="text-sm text-gray-800 mt-1">
              {request.target_reporting_officer_name
                ? `${request.target_reporting_officer_name} · ${request.target_reporting_officer_staff_id}`
                : 'Not recorded (legacy transfer)'}
            </p>
          </div>

          <div className="space-y-3">
            {(request.items || []).map((item) => (
              <div key={item.id} className="rounded-xl border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{item.worker_name}</p>
                    <p className="text-xs text-gray-500">
                      <TradeTransferPath from={item.from_trade_category_name} to={item.target_trade_name} />
                      {item.worker_department_name ? ` · ${item.worker_department_name}` : ''}
                    </p>
                  </div>
                  <StatusBadge
                    status={ITEM_BADGE_STATUS[item.status] || 'pending'}
                    label={item.status_display}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <ChainStep label="Current Trade Leader" status={item.ctl_status}
                    by={item.ctl_approver_name} at={item.ctl_decided_at} comments={item.ctl_comments} />
                  <ChainStep label="Worker" status={item.worker_status}
                    by={item.worker_name} at={item.worker_decided_at} comments={item.worker_comments} />
                  <ChainStep label="HOD" status={item.hod_status}
                    by={item.hod_approver_name} at={item.hod_decided_at} comments={item.hod_comments} />
                </div>
              </div>
            ))}
          </div>

          {canCancel && request.status === 'in_progress' && (
            <div className="flex justify-end">
              <button onClick={() => onCancel(request)} className="btn btn-danger btn-sm text-xs">
                Cancel Request
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Oversight row (HR "All Requests" tab) — read-only, fetches detail (items +
// audit trail) on first expand. Shows the requester, every worker's chain, and
// the modification log.
// ---------------------------------------------------------------------------
const OversightRow = ({ request, currentStaffId, onCancel, cancelling }) => {
  const [expanded, setExpanded] = useState(false);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['tt-oversight-detail', request.id],
    queryFn: () => staffAPI.getTradeTransferRequest(request.id).then((r) => r.data),
    enabled: expanded,
  });

  const counts = request.item_status_counts || {};
  const countChips = Object.entries(counts);
  // HR can cancel a request THEY initiated (backend enforces requester-only too).
  const canCancelOwn = (
    currentStaffId
    && request.requested_by_staff_id === currentStaffId
    && request.status === 'in_progress'
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-5 py-4 flex items-start justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{request.request_number || 'Draft'}</span>
            <StatusBadge status={request.status} label={request.status_display} />
          </div>
          <p className="text-sm text-gray-600 mt-1">
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              {request.worker_count} {request.worker_count === 1 ? 'worker' : 'workers'}
              <span className="text-gray-400">→</span>
              <TradeBadge name={request.target_trade_name} size="xs" />
            </span>
            {' · '}Requested by {request.requested_by_name}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Effective {formatDate(request.effective_date)}{' · '}Raised {formatDate(request.created_at)}
          </p>
          {countChips.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {countChips.map(([st, n]) => {
                const summary = ITEM_STATUS_SUMMARY[st];
                const label = summary
                  ? `${n} ${summary.phrase}`
                  : `${n} ${st.replace(/_/g, ' ')}`;
                return (
                  <StatusBadge
                    key={st}
                    status={summary?.badgeStatus || ITEM_BADGE_STATUS[st] || 'pending'}
                    label={label}
                  />
                );
              })}
            </div>
          )}
        </div>
        <ChevronRight className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-5 pb-4 border-t pt-4 space-y-4">
          {isLoading ? (
            <LoadingSpinner />
          ) : !detail ? (
            <p className="text-sm text-gray-500">Could not load details.</p>
          ) : (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</p>
                <p className="text-sm text-gray-800 mt-1">{detail.reason}</p>
              </div>

              {detail.status === 'cancelled' && (
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm">
                  <span className="font-medium text-gray-700">Cancelled</span>
                  {detail.cancelled_by_name ? ` by ${detail.cancelled_by_name}` : ''}
                  {detail.cancelled_at ? ` · ${formatDate(detail.cancelled_at)}` : ''}
                  {detail.cancellation_reason ? ` — “${detail.cancellation_reason}”` : ''}
                </div>
              )}

              <div className="space-y-3">
                {(detail.items || []).map((item) => (
                  <div key={item.id} className="rounded-xl border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{item.worker_name}</p>
                        <p className="text-xs text-gray-500">
                          <TradeTransferPath from={item.from_trade_category_name} to={item.target_trade_name} />
                          {item.worker_department_name ? ` · ${item.worker_department_name}` : ''}
                        </p>
                      </div>
                      <StatusBadge
                        status={ITEM_BADGE_STATUS[item.status] || 'pending'}
                        label={item.status_display}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      <ChainStep label="Current Trade Leader" status={item.ctl_status}
                        by={item.ctl_approver_name} at={item.ctl_decided_at} comments={item.ctl_comments} />
                      <ChainStep label="Worker" status={item.worker_status}
                        by={item.worker_name} at={item.worker_decided_at} comments={item.worker_comments} />
                      <ChainStep label="HOD" status={item.hod_status}
                        by={item.hod_approver_name} at={item.hod_decided_at} comments={item.hod_comments} />
                    </div>
                    {item.status === 'rejected' && item.rejected_gate_display && (
                      <p className="mt-2 text-xs text-red-600">Stopped at: {item.rejected_gate_display}</p>
                    )}
                    {item.applied_at && (
                      <p className="mt-2 text-xs text-green-700">Applied {formatDate(item.applied_at)}</p>
                    )}
                  </div>
                ))}
              </div>

              {(detail.modification_logs || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                    <History className="w-3.5 h-3.5" /> Audit trail
                  </p>
                  <div className="space-y-1.5">
                    {detail.modification_logs.map((log) => (
                      <div key={log.id} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="text-gray-400 flex-shrink-0">{formatDate(log.created_at)}</span>
                        <span>
                          <span className="font-medium text-gray-700">{log.modification_type_display}</span>
                          {log.modified_by_name ? ` · ${log.modified_by_name}` : ''}
                          {log.details?.reason ? ` — “${log.details.reason}”` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canCancelOwn && (
                <div className="flex justify-end">
                  <button
                    onClick={() => onCancel(request)}
                    disabled={cancelling}
                    className="btn btn-danger btn-sm text-xs disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel Request'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const OVERSIGHT_STATUS_FILTERS = [
  { id: 'all', label: 'All', status: null },
  { id: 'in_progress', label: 'In Progress', status: 'in_progress' },
  { id: 'completed', label: 'Completed', status: 'completed' },
  { id: 'cancelled', label: 'Cancelled', status: 'cancelled' },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const TradeTransferManagement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canRequestTradeTransfer = !!user?.can_request_trade_transfer;
  const canOversee = !!user?.can_oversee_trade_transfer;

  const defaultTab = canRequestTradeTransfer ? 'request' : (canOversee ? 'all' : 'inbox');
  const [tab, setTab] = useState(defaultTab);
  const [oversightFilter, setOversightFilter] = useState('all');
  const [decisionItem, setDecisionItem] = useState(null);
  const [showHrInitiate, setShowHrInitiate] = useState(false);

  // Form state
  const [targetTrade, setTargetTrade] = useState('');
  const [targetReportingOfficer, setTargetReportingOfficer] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [reason, setReason] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState([]);

  const { data: ledTrades = [] } = useQuery({
    queryKey: ['tt-led-trades'],
    queryFn: () => staffAPI.getTradeTransferLedTrades().then((r) => r.data),
    enabled: canRequestTradeTransfer,
  });

  const { data: workers = [], isLoading: workersLoading } = useQuery({
    queryKey: ['tt-transferable-workers'],
    queryFn: () => staffAPI.getTradeTransferTransferableWorkers().then((r) => r.data),
    enabled: canRequestTradeTransfer && tab === 'request',
  });

  const { data: reportingOfficers = [], isLoading: reportingOfficersLoading } = useQuery({
    queryKey: ['trade-transfer', 'eligible-reporting-officers', targetTrade],
    queryFn: () => staffAPI
      .getTradeTransferEligibleReportingOfficers(targetTrade)
      .then((r) => r.data),
    enabled: canRequestTradeTransfer && !!targetTrade,
  });

  // Auto-select the trade when the TL leads exactly one.
  useEffect(() => {
    if (ledTrades.length === 1 && !targetTrade) {
      setTargetTrade(String(ledTrades[0].id));
    }
  }, [ledTrades, targetTrade]);

  // Switching target trade drops any now-invalid (same-trade) selections.
  // targetTrade / trade_category_id are both UUID strings — compare as-is.
  useEffect(() => {
    if (!targetTrade) return;
    setTargetReportingOfficer('');
    setSelectedWorkers((prev) => prev.filter((sid) => {
      const w = workers.find((x) => x.staff_id === sid);
      return !w || w.trade_category_id !== targetTrade;
    }));
  }, [targetTrade, workers]);

  const { data: myRequests = [], isLoading: myReqLoading } = useQuery({
    queryKey: ['tt-my-requests'],
    queryFn: () => staffAPI.getTradeTransferMyRequests().then((r) => r.data),
    enabled: canRequestTradeTransfer,
  });

  const { data: inbox = [], isLoading: inboxLoading } = useQuery({
    queryKey: ['tt-inbox'],
    queryFn: () => staffAPI.getTradeTransferInbox().then((r) => r.data),
  });

  const oversightStatus = OVERSIGHT_STATUS_FILTERS.find((f) => f.id === oversightFilter)?.status || null;
  const { data: allRequestsResp, isLoading: allLoading } = useQuery({
    queryKey: ['tt-all-requests', oversightStatus],
    queryFn: () => staffAPI.getTradeTransferRequests(
      oversightStatus ? { status: oversightStatus } : {},
    ).then((r) => r.data),
    enabled: canOversee && tab === 'all',
  });
  const allRequests = useMemo(() => {
    const data = allRequestsResp?.results || allRequestsResp || [];
    return Array.isArray(data) ? data : [];
  }, [allRequestsResp]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['tt-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['tt-my-requests'] });
    queryClient.invalidateQueries({ queryKey: ['trade-transfer-dashboard-summary'] });
  };

  // Deep-link: ?tt=<item_id> opens the decision modal for that inbox item.
  React.useEffect(() => {
    const ttId = searchParams.get('tt');
    if (!ttId || inboxLoading) return;
    const target = inbox.find((i) => i.id === ttId);
    setSearchParams((prev) => { prev.delete('tt'); return prev; }, { replace: true });
    if (target) setDecisionItem(target);
  }, [searchParams, inbox, inboxLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const tradeOptions = useMemo(
    () => ledTrades.map((t) => ({ value: String(t.id), label: t.name })),
    [ledTrades],
  );

  const reportingOfficerOptions = useMemo(
    () => reportingOfficers.map((person) => ({
      value: person.staff_id,
      label: `${person.name} · ${person.staff_id}${person.designation ? ` · ${person.designation}` : ''}`,
    })),
    [reportingOfficers],
  );

  // Workers already in the selected destination trade can never succeed
  // (the backend rejects same-trade transfers), so keep them out of the picker.
  // targetTrade / trade_category_id are both UUID strings — compare as-is.
  const availableWorkers = useMemo(() => {
    if (!targetTrade) return workers;
    return workers.filter((w) => w.trade_category_id !== targetTrade);
  }, [workers, targetTrade]);

  const submitMutation = useMutation({
    mutationFn: () => staffAPI.createTradeTransferRequest({
      target_trade_category_id: targetTrade,
      target_reporting_officer_id: targetReportingOfficer,
      effective_date: toISODate(effectiveDate),
      reason: reason.trim(),
      worker_ids: selectedWorkers,
    }),
    onSuccess: () => {
      toast.success('Transfer request submitted.');
      if (ledTrades.length > 1) setTargetTrade('');
      setTargetReportingOfficer('');
      setEffectiveDate(null); setReason(''); setSelectedWorkers([]);
      queryClient.invalidateQueries({ queryKey: ['tt-my-requests'] });
      setTab('my-requests');
    },
    onError: (err) => toast.error(extractTtErrorMessage(err, 'Failed to submit request.')),
  });

  const cancelMutation = useMutation({
    mutationFn: (request) => staffAPI.cancelTradeTransferRequest(request.id, {}),
    onSuccess: () => {
      toast.success('Request cancelled.');
      queryClient.invalidateQueries({ queryKey: ['tt-my-requests'] });
      queryClient.invalidateQueries({ queryKey: ['tt-all-requests'] });
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Failed to cancel.'),
  });

  const canSubmit = targetTrade && targetReportingOfficer
    && effectiveDate && reason.trim() && selectedWorkers.length > 0;

  const PIPELINE_STEPS = [
    { icon: Send,             label: 'You submit',         desc: 'Fill out this form and submit the request.', gate: false },
    { icon: ClipboardCheck,   label: 'Current TL reviews', desc: 'Can approve or reject releasing the worker.', gate: true },
    { icon: UserCheck,        label: 'Worker acknowledges', desc: 'Can accept or decline the transfer.',       gate: true },
    { icon: ShieldCheck,      label: 'HOD approves',       desc: 'Can approve or reject as final sign-off.',   gate: true },
    { icon: ArrowRightCircle, label: 'Transfer applied',   desc: 'Trade and RO change together on the date.', gate: false },
  ];

  const tabs = [
    canRequestTradeTransfer && { id: 'request', label: 'Request Transfer', icon: Send },
    canRequestTradeTransfer && { id: 'my-requests', label: 'My Requests', icon: Repeat },
    { id: 'inbox', label: `My Inbox${inbox.length ? ` (${inbox.length})` : ''}`, icon: Inbox },
    canOversee && { id: 'all', label: 'All Requests', icon: ClipboardList },
  ].filter(Boolean);

  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-subsection-header">Worker Trade Transfer</h1>
              <p className="text-body mt-1">
                Request to move workers between trades and track every acknowledgement.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs + Initiate Transfer (CTA sits with the primary view switcher) */}
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6">
          <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth min-w-0">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`tab-btn whitespace-nowrap flex-shrink-0 md:flex-shrink ${active ? 'active' : ''}`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
          {canOversee && (
            <button
              type="button"
              onClick={() => {
                setTab('all');
                setShowHrInitiate(true);
              }}
              className="btn btn-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
              title="Initiate Transfer"
              aria-label="Initiate Transfer"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Initiate Transfer</span>
            </button>
          )}
        </div>

        {/* Request Transfer */}
        {tab === 'request' && canRequestTradeTransfer && (
          <div className="space-y-5">
            {/* Pipeline stepper — shows the per-worker approval journey */}
            <div className="bg-white rounded-2xl shadow-sm border p-5">
              <div className="flex items-baseline justify-between gap-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Approval pipeline</h3>
                <span className="text-xs text-gray-400 hidden sm:inline">per worker — each worker is processed independently</span>
              </div>
              <div className="flex items-start">
                {PIPELINE_STEPS.map((step, i) => {
                  const Icon = step.icon;
                  const isFirst = i === 0;
                  const isLast = i === PIPELINE_STEPS.length - 1;
                  return (
                    <React.Fragment key={i}>
                      <div className="flex flex-col items-center flex-1 min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isFirst ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className={`mt-2 text-xs font-medium text-center leading-tight ${
                          isFirst ? 'text-blue-700' : 'text-gray-600'
                        }`}>
                          {step.label}
                        </p>
                        <p className="mt-0.5 text-[11px] text-gray-400 text-center leading-tight hidden lg:block px-1">
                          {step.desc}
                        </p>
                        {step.gate && (
                          <span className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 rounded-full px-1.5 py-0.5 leading-none hidden sm:inline-flex">
                            <XCircle className="w-2.5 h-2.5" /> can reject
                          </span>
                        )}
                      </div>
                      {!isLast && (
                        <div className="flex-shrink-0 w-8 lg:w-12 h-px bg-gray-200 mt-[18px]" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <p className="mt-4 text-xs text-gray-500 sm:hidden">
                Each worker is processed independently. A rejection at any gate stops only that worker's transfer.
              </p>
              <p className="mt-3 text-xs text-gray-500 hidden sm:block">
                If any gate rejects, only that worker's transfer is stopped — the rest of the batch continues. You'll be notified of each decision.
              </p>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-5">
              {/* Row 1: Trade + Date side-by-side on desktop */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {ledTrades.length <= 1 ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transferring into your trade
                    </label>
                    <div className="field-static h-11 flex items-center text-sm text-gray-900">
                      {ledTrades[0]?.name || '—'}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Which of your trades? <span className="text-red-600">*</span>
                    </label>
                    <FormSelect
                      options={tradeOptions}
                      value={targetTrade}
                      onChange={setTargetTrade}
                      placeholder="Select one of the trades you lead…"
                      theme="employee"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective date <span className="text-red-600">*</span>
                  </label>
                  <DatePickerField
                    theme="employee"
                    selected={effectiveDate}
                    onChange={setEffectiveDate}
                    minDate={new Date()}
                    placeholderText="Select date"
                    isClearable
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Reporting Officer <span className="text-red-600">*</span>
                </label>
                <FormSelect
                  options={reportingOfficerOptions}
                  value={targetReportingOfficer}
                  onChange={setTargetReportingOfficer}
                  placeholder={
                    reportingOfficersLoading
                      ? 'Loading Reporting Officers…'
                      : targetTrade
                        ? 'Select the destination trade RO…'
                        : 'Choose a destination trade first…'
                  }
                  theme="employee"
                  disabled={!targetTrade || reportingOfficersLoading}
                  required
                />
                {targetTrade && !reportingOfficersLoading && reportingOfficerOptions.length === 0 && (
                  <p className="mt-1.5 text-xs text-amber-700">
                    No eligible Reporting Officer is configured for this trade.
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  The worker’s Reporting Officer changes with the trade on the effective date.
                </p>
              </div>

              {/* Worker multi-select — full width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workers to transfer in <span className="text-red-600">*</span>
                </label>
                <SearchableMultiCombobox
                  items={availableWorkers}
                  value={selectedWorkers}
                  onChange={setSelectedWorkers}
                  valueFn={(w) => w.staff_id}
                  displayFn={(w) => `${w.name} · ${w.trade_category_name}`}
                  searchFn={(w, term) =>
                    `${w.name} ${w.staff_id} ${w.trade_category_name}`.toLowerCase().includes(term)}
                  groupFn={(w) => w.trade_category_name}
                  placeholder="Select workers from any trade…"
                  searchPlaceholder="Search by name, staff ID, or trade…"
                  icon={Users}
                  iconColor="text-blue-700"
                  theme="employee"
                  variant="form"
                  loading={workersLoading}
                  showChips
                />
              </div>

              {/* Row 3: Reason — full width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for transfer <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Explain why these workers are being transferred…"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => submitMutation.mutate()}
                  disabled={!canSubmit || submitMutation.isPending}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {submitMutation.isPending ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* My Requests */}
        {tab === 'my-requests' && canRequestTradeTransfer && (
          <div className="space-y-3">
            {myReqLoading ? (
              <LoadingSpinner />
            ) : myRequests.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center text-gray-500 text-sm">
                You haven’t raised any transfer requests yet.
              </div>
            ) : (
              myRequests.map((req) => (
                <RequestCard
                  key={req.id}
                  request={req}
                  canCancel
                  onCancel={(r) => cancelMutation.mutate(r)}
                />
              ))
            )}
          </div>
        )}

        {/* Inbox */}
        {tab === 'inbox' && (
          <div className="space-y-3">
            {inboxLoading ? (
              <LoadingSpinner />
            ) : inbox.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center text-gray-500 text-sm">
                Nothing awaiting your action.
              </div>
            ) : (
              inbox.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl shadow-sm border p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{item.worker_name}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                        <AlertCircle className="w-3 h-3" /> {GATE_LABEL[item.status] || 'Action'} step
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      <TradeTransferPath from={item.from_trade_category_name} to={item.target_trade_name} />
                      {' · '}Effective {formatDate(item.effective_date)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Requested by {item.requested_by_name}</p>
                  </div>
                  <button
                    onClick={() => setDecisionItem(item)}
                    className="btn btn-primary btn-sm text-xs flex-shrink-0"
                  >
                    Review
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* All Requests — HR oversight (initiate CTA lives on the primary tab row) */}
        {tab === 'all' && canOversee && (
          <div className="space-y-4">
            <PillToggle
              options={OVERSIGHT_STATUS_FILTERS.map((f) => ({
                id: f.id,
                label: f.label,
              }))}
              value={oversightFilter}
              onChange={setOversightFilter}
              theme="employee"
              variant="text"
              className="w-fit max-w-full"
              ariaLabel="Request status filter"
            />

            {allLoading ? (
              <LoadingSpinner />
            ) : allRequests.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center text-gray-500 text-sm">
                No trade transfer requests{oversightStatus ? ` that are ${oversightFilter.replace(/_/g, ' ')}` : ''} yet.
              </div>
            ) : (
              <div className="space-y-3">
                {allRequests.map((req) => (
                  <OversightRow
                    key={req.id}
                    request={req}
                    currentStaffId={user?.staff_id}
                    onCancel={(r) => cancelMutation.mutate(r)}
                    cancelling={cancelMutation.isPending}
                  />
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 text-center pt-2">
              HR can direct transfers in either direction • HR is notified automatically when each worker's transfer is applied.
            </p>
          </div>
        )}
      </div>

      {decisionItem && (
        <DecisionModal
          item={decisionItem}
          onClose={() => setDecisionItem(null)}
          onDecided={refreshAll}
        />
      )}

      {showHrInitiate && (
        <HRInitiateModal
          onClose={() => setShowHrInitiate(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['tt-all-requests'] })}
        />
      )}
    </DashboardLayout>
  );
};

export default TradeTransferManagement;
